import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { catchError, debounceTime, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { ListSchedulerCrew$Params } from '../../api/scheduler/functions';
import { CrewStatus } from '../../api/scheduler/models/crew-status';
import { Rank } from '../../api/scheduler/models/rank';
import { Rotation } from '../../api/scheduler/models/rotation';
import { SchedulerCrewDto } from '../../api/scheduler/models/scheduler-crew-dto';
import { WingType } from '../../api/scheduler/models/wing-type';
import { SchedulerCrewApiService } from '../../features/edit-crew/services/scheduler-crew-api.service';
import { schedulerCrewEditableFieldsEqual } from '../../features/edit-crew/utils/scheduler-crew.mapper';
import { userFacingApiError } from '../master-schedule/master-schedule-user-errors';
import { MasterScheduleActions } from '../master-schedule/master-schedule.actions';
import { ToastActions } from '../toast/toast.actions';
import { SchedulerCrewActions } from './scheduler-crew.actions';
import { selectSchedulerCrewState } from './scheduler-crew.selectors';

@Injectable()
export class SchedulerCrewEffects {
  private readonly actions$ = inject(Actions);
  private readonly api = inject(SchedulerCrewApiService);
  private readonly store = inject(Store);

  load$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.load, SchedulerCrewActions.reset),
      withLatestFrom(this.store.select(selectSchedulerCrewState)),
      switchMap(([, state]) =>
        this.api.list(listParams(state.query)).pipe(
          map((response) =>
            SchedulerCrewActions.loadSuccess({
              items: response.data ?? [],
              page: response.page ?? null,
              requestId: state.listRequestId,
            })
          ),
          catchError((error) =>
            of(
              SchedulerCrewActions.loadFailure({
                error: userFacingApiError(error),
                requestId: state.listRequestId,
              })
            )
          )
        )
      )
    )
  );

  searchLoad$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.setSearch),
      debounceTime(300),
      map(({ search }) => SchedulerCrewActions.load({ query: { search, page: 0 } }))
    )
  );

  update$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.update),
      withLatestFrom(this.store.select(selectSchedulerCrewState)),
      mergeMap(([{ id, rank, wingType, rotation, previous }, state]) => {
        const requestId = state.updateRequests[id];

        if (schedulerCrewEditableFieldsEqual(previous, { ...previous, rank, wingType, rotation })) {
          return EMPTY;
        }

        return this.api.update(id, { rank, wingType, rotation }).pipe(
          mergeMap((response) => {
            const returned = Array.isArray(response.data) ? response.data[0] : undefined;
            const crew: SchedulerCrewDto = {
              ...previous,
              ...returned,
              id,
              rank,
              wingType,
              rotation,
            };
            return [
              SchedulerCrewActions.updateSuccess({ id, crew, requestId }),
              MasterScheduleActions.refresh(),
            ];
          }),
          catchError((error) =>
            of(
              SchedulerCrewActions.updateFailure({
                id,
                requestId,
                previous,
                error: userFacingApiError(error),
              })
            )
          )
        );
      })
    )
  );

  updateStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.updateStatus),
      withLatestFrom(this.store.select(selectSchedulerCrewState)),
      mergeMap(([{ id, status, previous }, state]) => {
        const requestId = state.updateRequests[id];

        if (previous.status === status) {
          return EMPTY;
        }

        return this.api.updateStatus(id, { status }).pipe(
          mergeMap((response) => {
            const returned = Array.isArray(response.data) ? response.data[0] : undefined;
            const crew: SchedulerCrewDto = {
              ...previous,
              ...returned,
              id,
              status,
            };
            return [
              SchedulerCrewActions.updateStatusSuccess({ id, crew, requestId }),
              MasterScheduleActions.refresh(),
            ];
          }),
          catchError((error) =>
            of(
              SchedulerCrewActions.updateStatusFailure({
                id,
                requestId,
                previous,
                error: userFacingApiError(error),
              })
            )
          )
        );
      })
    )
  );

  updateFailureToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.updateFailure, SchedulerCrewActions.updateStatusFailure),
      map(({ error }) =>
        ToastActions.showToast({
          severity: 'error',
          summary: 'Crew update failed',
          detail: error,
        })
      )
    )
  );

  loadFailureToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulerCrewActions.loadFailure),
      map(({ error }) =>
        ToastActions.showToast({
          severity: 'error',
          summary: 'Crew list failed',
          detail: error,
        })
      )
    )
  );
}

function listParams(query: {
  search: string;
  rotation: Rotation | '';
  rank: Rank | '';
  wingType: WingType | '';
  status: CrewStatus | '';
  sort: string;
  page: number;
  size: number;
}): ListSchedulerCrew$Params {
  const params: ListSchedulerCrew$Params = {
    page: query.page,
    size: query.size,
    sort: query.sort,
  };
  if (query.rotation) {
    params.rotation = query.rotation;
  }
  if (query.rank) {
    params.rank = query.rank;
  }
  if (query.wingType) {
    params.wingType = query.wingType;
  }
  if (query.status) {
    params.status = query.status;
  }
  const search = query.search.trim();
  if (search) {
    params.search = search;
  }
  return params;
}
